/**
 * AP-10 — gemeinsamer Rahmen der beiden Protokoll-Detailfenster
 * (FM-WIN-18 ISOBUS, FM-WIN-19 CANopen). Beide zeigen dasselbe: eine Zeile
 * Rohbytes und eine Field/Value-Liste, die bei JEDEM passenden Frame komplett
 * neu gefuellt wird — es gibt keine Historie, nur den letzten Frame
 * (IsobusDetailWindow.cpp:236-249).
 */

import './viewsD.css';
import { createDataTable } from './dataTable';
import { createFloatingPanel } from './filters/FloatingPanel';

/** Startzustand vor dem ersten Frame (IsobusDetailWindow.cpp:103). */
const PLACEHOLDER = '-- no data yet --';

export interface DetailUpdate {
  dlc: number;
  data: readonly number[];
  summary: string;
  fields: ReadonlyArray<{ label: string; value: string }>;
}

export interface DetailWindowHandle {
  show(): void;
  /** Der Name kommt erst mit dem ersten dekodierten Frame (siehe viewsD.ts). */
  setTitle(title: string): void;
  update(update: DetailUpdate): void;
}

/** `"Raw: "` + je Byte `"%02X "` (IsobusDetailWindow.cpp:240-243). */
function formatRaw(data: readonly number[], dlc: number): string {
  let out = 'Raw: ';
  for (let i = 0; i < dlc && i < data.length; i += 1) {
    out += `${(data[i] ?? 0).toString(16).toUpperCase().padStart(2, '0')} `;
  }
  return out;
}

export function createDetailWindow(title: string): DetailWindowHandle {
  const panel = createFloatingPanel(title);
  panel.element.classList.add('cm-panel-window--medium');

  const raw = document.createElement('div');
  raw.className = 'cm-detail__raw';
  raw.textContent = PLACEHOLDER;

  const summary = document.createElement('div');
  summary.className = 'cm-view__summary';

  const table = createDataTable({ columns: ['Field', 'Value'], emptyText: PLACEHOLDER });
  table.element.classList.add('cm-table-scroll--values');

  const layout = document.createElement('div');
  layout.className = 'cm-view';
  layout.append(raw, summary, table.element);
  panel.body.append(layout);

  // `createFloatingPanel` nimmt den Titel nur bei der Erzeugung entgegen; der
  // Name der Botschaft steht aber erst mit dem ersten dekodierten Frame fest.
  const titleElement = panel.element.querySelector('.cm-panel-window__header span');

  return {
    show: () => panel.show(),
    setTitle(next) {
      if (titleElement !== null) titleElement.textContent = next;
    },
    update(update) {
      raw.textContent = formatRaw(update.data, update.dlc);
      summary.textContent = update.summary;
      table.setRows(update.fields.map((field) => [field.label, field.value]));
    },
  };
}
